import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { Plus, Trash2 } from 'lucide-react';
import BrandSelect from '../ui/BrandSelect';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Actions as CONN_ACTIONS } from '../../store/actions/connectionActions';
import { Actions as FACILITY_ACTIONS } from '../../store/actions/facilityActions';
import ACTIONS from '../../store/action';
import { mappingRowSelect } from '../utils/ReactSelectStyles';

/**
 * Prefer stable provider identifiers for match.by — users never choose this.
 * @param {object} raw - dimension-values row
 */
const matchFromDimensionValue = (raw) => {
  if (!raw) return { by: 'name', value: '' };
  if (raw.code != null && String(raw.code) !== '') return { by: 'code', value: String(raw.code) };
  if (raw.externalId != null && String(raw.externalId) !== '') {
    return { by: 'externalId', value: String(raw.externalId) };
  }
  if (raw.name != null && String(raw.name) !== '') return { by: 'name', value: String(raw.name) };
  return { by: 'name', value: String(raw.value || '') };
};

const emptyCategoryMap = (defaultDimensionType = 'gl_account') => ({
  source: { dimensionType: defaultDimensionType, match: { by: 'code', value: '' } },
  target: { entityId: '', mappingStrategy: 'ai' },
});

const emptyFacilityMap = (defaultDimensionType = 'cost_centre') => ({
  source: { dimensionType: defaultDimensionType, match: { by: 'name', value: '' } },
  facilityId: '',
});

const dimensionFetchKey = (connectorId, dimensions = []) =>
  `${connectorId}::${dimensions
    .filter((d) => d.listValuesCapability)
    .map((d) => `${d.type}:${d.platformSlot || ''}`)
    .sort()
    .join('|')}`;

const findValueOption = (options, match) => {
  if (!match?.value) return null;
  const v = String(match.value);
  return (
    options.find((o) => {
      const raw = o.raw || {};
      return (
        String(o.value) === v ||
        String(raw.code || '') === v ||
        String(raw.externalId || '') === v ||
        String(raw.name || '') === v ||
        String(raw.value || '') === v
      );
    }) || null
  );
};

const MappingEditor = ({
  connectorId,
  dimensionManifest,
  dimensionValuesByKey,
  mappingConfig,
  mappingConfigDraft,
  loading,
  facilities,
  entities,
  getDimensionManifest,
  getDimensionValues,
  getMappingConfig,
  putMappingConfig,
  getFacilities,
  getEntities,
}) => {
  const [draft, setDraft] = useState({
    categoryMaps: [],
    facilityMaps: [],
    defaults: { unmappedStrategy: 'ai' },
  });
  const [draftBanner, setDraftBanner] = useState(false);
  const fetchedDimsRef = useRef('');

  useEffect(() => {
    getDimensionManifest(connectorId);
    getMappingConfig(connectorId);
    getFacilities();
    getEntities();
    fetchedDimsRef.current = '';
  }, [connectorId]);

  useEffect(() => {
    if (mappingConfig) {
      setDraft({
        categoryMaps: (mappingConfig.categoryMaps || []).map((row) => ({
          ...row,
          target: {
            ...row.target,
            // New UX: treat hybrid/missing as AI for editing; keep direct if already set.
            mappingStrategy: row.target?.mappingStrategy === 'direct' ? 'direct' : 'ai',
          },
        })),
        facilityMaps: mappingConfig.facilityMaps || [],
        defaults: {
          unmappedStrategy: mappingConfig.defaults?.unmappedStrategy === 'direct' ? 'direct' : 'ai',
        },
      });
    }
  }, [mappingConfig]);

  useEffect(() => {
    setDraftBanner(Boolean(mappingConfigDraft));
  }, [mappingConfigDraft]);

  useEffect(() => {
    const dims = dimensionManifest?.dimensions || [];
    if (!dims.length) return;
    const key = dimensionFetchKey(connectorId, dims);
    if (fetchedDimsRef.current === key) return;
    fetchedDimsRef.current = key;
    dims.forEach((d) => {
      if (d.listValuesCapability) {
        getDimensionValues(connectorId, d.type, d.platformSlot);
      }
    });
  }, [dimensionManifest, connectorId, getDimensionValues]);

  const dimensionOptions = useMemo(
    () =>
      (dimensionManifest?.dimensions || []).map((d) => ({
        value: d.type,
        label: d.uiLabel || d.type,
        dimension: d,
      })),
    [dimensionManifest],
  );

  const defaultCategoryDim = dimensionOptions[0]?.value || 'gl_account';
  const defaultFacilityDim =
    dimensionOptions.find((d) => d.value === 'cost_centre')?.value || dimensionOptions[0]?.value || 'cost_centre';

  const entityOptions = useMemo(
    () =>
      (entities || []).map((e) => ({
        value: String(e.id || e.value),
        label: e.name || e.label || String(e.id),
      })),
    [entities],
  );

  const facilityOptions = useMemo(
    () =>
      (facilities || []).map((f) => ({
        value: f.id,
        label: f.name || f.id,
      })),
    [facilities],
  );

  const valuesFor = (dimensionType, platformSlot) => {
    const key = platformSlot ? `${dimensionType}:${platformSlot}` : dimensionType;
    return (dimensionValuesByKey?.[key] || []).map((v) => ({
      value: v.value || v.code || v.externalId || v.name,
      label: v.label || v.name || v.code || v.value,
      raw: v,
    }));
  };

  const importDraft = () => {
    if (!mappingConfigDraft) return;
    setDraft({
      categoryMaps: (mappingConfigDraft.categoryMaps || []).map((row) => ({
        ...row,
        target: { ...row.target, mappingStrategy: row.target?.mappingStrategy === 'direct' ? 'direct' : 'ai' },
      })),
      facilityMaps: mappingConfigDraft.facilityMaps || [],
      defaults: { unmappedStrategy: 'ai' },
    });
    setDraftBanner(false);
  };

  const updateCategoryMap = (index, patch) => {
    setDraft((prev) => {
      const categoryMaps = [...prev.categoryMaps];
      categoryMaps[index] = { ...categoryMaps[index], ...patch };
      return { ...prev, categoryMaps };
    });
  };

  const updateFacilityMap = (index, patch) => {
    setDraft((prev) => {
      const facilityMaps = [...prev.facilityMaps];
      facilityMaps[index] = { ...facilityMaps[index], ...patch };
      return { ...prev, facilityMaps };
    });
  };

  const save = () => {
    const payload = {
      ...draft,
      defaults: { unmappedStrategy: 'ai' },
      categoryMaps: (draft.categoryMaps || []).map((row) => ({
        ...row,
        target: {
          ...row.target,
          mappingStrategy: row.target?.mappingStrategy === 'direct' ? 'direct' : 'ai',
        },
      })),
    };
    putMappingConfig(connectorId, payload);
  };

  return (
    <Card className='bg-card text-card-foreground border-border'>
      <CardHeader className='flex flex-row flex-wrap items-center justify-between gap-4'>
        <div>
          <CardTitle className='text-base'>Dimension mapping</CardTitle>
          <p className='mt-1 text-sm text-muted-foreground'>
            Tell us which provider accounts or tracking options map to your CYF usage categories and facilities. Spend
            lines then use AI to match emission factors.
          </p>
        </div>
        <Button variant='outline' loading={loading?.mapping} onClick={save}>
          Save mapping
        </Button>
      </CardHeader>
      <CardContent className='space-y-8'>
        {draftBanner && mappingConfigDraft && (
          <div className='flex flex-col gap-2 rounded-md border border-warning bg-warning-bg p-3 sm:flex-row sm:items-center sm:justify-between'>
            <p className='text-sm text-warning'>A mapping draft is available from migration. Import it into the editor, then save.</p>
            <Button variant='outline' size='sm' onClick={importDraft}>
              Import draft
            </Button>
          </div>
        )}

        <div className='space-y-3'>
          <div className='flex items-center justify-between gap-2'>
            <div>
              <h3 className='text-sm font-semibold text-foreground'>Category maps</h3>
              <p className='text-xs text-muted-foreground'>
                Map a provider field (e.g. Account or Tracking category) to a CYF usage entity.
              </p>
            </div>
            <Button
              variant='outline'
              size='sm'
              onClick={() =>
                setDraft((prev) => ({
                  ...prev,
                  categoryMaps: [...prev.categoryMaps, emptyCategoryMap(defaultCategoryDim)],
                }))
              }>
              <Plus size={16} strokeWidth={1.5} className='mr-1' />
              Add
            </Button>
          </div>

          {draft.categoryMaps.map((row, index) => {
            const dimOpt = dimensionOptions.find((d) => d.value === row.source?.dimensionType) || null;
            const valueOpts = valuesFor(row.source?.dimensionType, row.source?.platformSlot);
            const selectedValue = findValueOption(valueOpts, row.source?.match);
            const entity = entityOptions.find((e) => e.value === String(row.target?.entityId || '')) || null;
            const sourceLabel = dimOpt?.label || 'Source value';

            return (
              <div key={index} className='rounded-md border border-border p-3'>
                <div className='flex flex-col gap-3 xl:grid xl:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_minmax(0,1.4fr)_auto] xl:items-end'>
                  <div className='min-w-0'>
                    <label className='mb-1 block text-xs font-medium text-muted-foreground'>Provider field</label>
                    <BrandSelect
                      styles={mappingRowSelect}
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      menuPosition='fixed'
                      options={dimensionOptions}
                      value={dimOpt}
                      placeholder='e.g. Account'
                      onChange={(opt) =>
                        updateCategoryMap(index, {
                          source: {
                            dimensionType: opt.value,
                            platformSlot: opt.dimension?.platformSlot,
                            match: { by: 'code', value: '' },
                          },
                        })
                      }
                    />
                  </div>
                  <div className='min-w-0'>
                    <label className='mb-1 block text-xs font-medium text-muted-foreground'>{sourceLabel}</label>
                    <BrandSelect
                      styles={mappingRowSelect}
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      menuPosition='fixed'
                      options={valueOpts}
                      value={selectedValue}
                      placeholder={`Select ${String(sourceLabel).toLowerCase()}`}
                      onChange={(opt) =>
                        updateCategoryMap(index, {
                          source: {
                            ...row.source,
                            match: matchFromDimensionValue(opt.raw),
                          },
                        })
                      }
                    />
                  </div>
                  <div className='min-w-0'>
                    <label className='mb-1 block text-xs font-medium text-muted-foreground'>CYF usage entity</label>
                    <BrandSelect
                      styles={mappingRowSelect}
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      menuPosition='fixed'
                      options={entityOptions}
                      value={entity}
                      placeholder='Select entity'
                      onChange={(opt) =>
                        updateCategoryMap(index, {
                          target: { entityId: opt.value, mappingStrategy: 'ai' },
                        })
                      }
                    />
                  </div>
                  <div className='flex shrink-0 items-end self-end'>
                    <Button
                      variant='ghost'
                      size='icon'
                      className='text-destructive'
                      aria-label='Remove category map'
                      onClick={() =>
                        setDraft((prev) => ({
                          ...prev,
                          categoryMaps: prev.categoryMaps.filter((_, i) => i !== index),
                        }))
                      }>
                      <Trash2 size={16} strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}

          {!draft.categoryMaps.length && (
            <p className='text-sm text-muted-foreground'>
              No category maps yet. Add one to link provider accounts or tracking options to CYF entities.
            </p>
          )}
        </div>

        <div className='space-y-3'>
          <div className='flex items-center justify-between gap-2'>
            <div>
              <h3 className='text-sm font-semibold text-foreground'>Facility maps</h3>
              <p className='text-xs text-muted-foreground'>
                Optionally map a tracking option (or other dimension) to a CYF facility.
              </p>
            </div>
            <Button
              variant='outline'
              size='sm'
              onClick={() =>
                setDraft((prev) => ({
                  ...prev,
                  facilityMaps: [...prev.facilityMaps, emptyFacilityMap(defaultFacilityDim)],
                }))
              }>
              <Plus size={16} strokeWidth={1.5} className='mr-1' />
              Add
            </Button>
          </div>

          {draft.facilityMaps.map((row, index) => {
            const dimOpt = dimensionOptions.find((d) => d.value === row.source?.dimensionType) || null;
            const valueOpts = valuesFor(row.source?.dimensionType, row.source?.platformSlot);
            const selectedValue = findValueOption(valueOpts, row.source?.match);
            const facility = facilityOptions.find((f) => f.value === row.facilityId) || null;
            const sourceLabel = dimOpt?.label || 'Source value';

            return (
              <div key={index} className='rounded-md border border-border p-3'>
                <div className='flex flex-col gap-3 xl:grid xl:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_minmax(0,1.4fr)_auto] xl:items-end'>
                  <div className='min-w-0'>
                    <label className='mb-1 block text-xs font-medium text-muted-foreground'>Provider field</label>
                    <BrandSelect
                      styles={mappingRowSelect}
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      menuPosition='fixed'
                      options={dimensionOptions}
                      value={dimOpt}
                      placeholder='e.g. Tracking category'
                      onChange={(opt) =>
                        updateFacilityMap(index, {
                          source: {
                            dimensionType: opt.value,
                            platformSlot: opt.dimension?.platformSlot,
                            match: { by: 'name', value: '' },
                          },
                        })
                      }
                    />
                  </div>
                  <div className='min-w-0'>
                    <label className='mb-1 block text-xs font-medium text-muted-foreground'>{sourceLabel}</label>
                    <BrandSelect
                      styles={mappingRowSelect}
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      menuPosition='fixed'
                      options={valueOpts}
                      value={selectedValue}
                      placeholder={`Select ${String(sourceLabel).toLowerCase()}`}
                      onChange={(opt) =>
                        updateFacilityMap(index, {
                          source: {
                            ...row.source,
                            match: matchFromDimensionValue(opt.raw),
                          },
                        })
                      }
                    />
                  </div>
                  <div className='min-w-0'>
                    <label className='mb-1 block text-xs font-medium text-muted-foreground'>CYF facility</label>
                    <BrandSelect
                      styles={mappingRowSelect}
                      menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                      menuPosition='fixed'
                      options={facilityOptions}
                      value={facility}
                      placeholder='Select facility'
                      onChange={(opt) => updateFacilityMap(index, { facilityId: opt.value })}
                    />
                  </div>
                  <div className='flex shrink-0 items-end self-end'>
                    <Button
                      variant='ghost'
                      size='icon'
                      className='text-destructive'
                      aria-label='Remove facility map'
                      onClick={() =>
                        setDraft((prev) => ({
                          ...prev,
                          facilityMaps: prev.facilityMaps.filter((_, i) => i !== index),
                        }))
                      }>
                      <Trash2 size={16} strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}

          {!draft.facilityMaps.length && (
            <p className='text-sm text-muted-foreground'>No facility maps yet. Optional — add only if you allocate spend by site.</p>
          )}
        </div>

        <div className='flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between'>
          <p className='text-sm text-muted-foreground'>Save when your category and facility maps look right.</p>
          <Button loading={loading?.mapping} onClick={save}>
            Save mapping
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const mapStateToProps = (state) => ({
  dimensionManifest: state.connections?.dimensionManifest,
  dimensionValuesByKey: state.connections?.dimensionValuesByKey,
  mappingConfig: state.connections?.mappingConfig,
  mappingConfigDraft: state.connections?.mappingConfigDraft,
  loading: state.connections?.loading,
  facilities: state.facility?.list || [],
  entities: state.entities || [],
});

const mapDispatchToProps = (dispatch) => ({
  getDimensionManifest: (id) => dispatch(CONN_ACTIONS.getDimensionManifest(id)),
  getDimensionValues: (id, type, slot) => dispatch(CONN_ACTIONS.getDimensionValues(id, type, slot)),
  getMappingConfig: (id) => dispatch(CONN_ACTIONS.getMappingConfig(id)),
  putMappingConfig: (id, config) => dispatch(CONN_ACTIONS.putMappingConfig(id, config)),
  getFacilities: () => dispatch(FACILITY_ACTIONS.getFacilities()),
  getEntities: () => dispatch(ACTIONS.getEntities()),
});

export default connect(mapStateToProps, mapDispatchToProps)(MappingEditor);
